import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Chip
} from '@mui/material';
import { Add as AddIcon, Science as ScienceIcon } from '@mui/icons-material';
import { useEntitySearch } from '../../hooks/useEntitySearch';
import { useNotification } from '../../context/NotificationContext';
import {
  searchTechnologies,
  getTechnology,
  createTechnology,
  updateTechnology,
  deleteTechnology
} from '../../api/technologies';
import { Technology, TechnologyCreate, TechnologyUpdate, TechnologyListItem } from '../../types/technology';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import EntitySearchResults from '../../components/entities/EntitySearchResults/EntitySearchResults';
import TechnologyForm from '../../components/entities/TechnologyForm/TechnologyForm';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';

const TechnologiesPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { results, loading: searchLoading, error: searchError, search, clearResults } = useEntitySearch<TechnologyListItem>(searchTechnologies);
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTechnology, setSelectedTechnology] = useState<Technology | null>(null);
  const [loadingTechnology, setLoadingTechnology] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load technology from URL parameter if present
  useEffect(() => {
    const technologyId = searchParams.get('id');
    if (technologyId) {
      setLoadingTechnology(true);
      setError(null);

      getTechnology(technologyId)
        .then(technology => {
          setSelectedTechnology(technology);
          // Clear the ID from URL after loading
          setSearchParams({});
        })
        .catch(err => {
          setError(err.response?.data?.detail || err.message || 'Failed to load technology');
          showNotification('Failed to load technology', 'error');
          // Clear the invalid ID from URL
          setSearchParams({});
        })
        .finally(() => {
          setLoadingTechnology(false);
        });
    }
  }, [searchParams, setSearchParams, showNotification]);

  const handleSearch = useCallback(async (query: string) => {
    await search(query);
  }, [search]);

  const handleSelectTechnology = async (item: TechnologyListItem) => {
    setLoadingTechnology(true);
    setError(null);

    try {
      const technology = await getTechnology(item.id);
      setSelectedTechnology(technology);
      clearResults(); // Clear search results after selection
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load technology');
      showNotification('Failed to load technology', 'error');
    } finally {
      setLoadingTechnology(false);
    }
  };

  const handleNewTechnology = () => {
    setSelectedTechnology(null);
    setError(null);
  };

  const handleSave = async (data: TechnologyCreate | TechnologyUpdate, id?: string) => {
    try {
      if (id) {
        const updated = await updateTechnology(id, data as TechnologyUpdate);
        setSelectedTechnology(updated);
        showNotification('Technology updated successfully', 'success');
      } else {
        const created = await createTechnology(data as TechnologyCreate);
        setSelectedTechnology(created);
        showNotification('Technology created successfully', 'success');
      }
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err;
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteTechnology(id);
      setSelectedTechnology(null);
      showNotification('Technology deleted successfully', 'success');
      if (results.length > 0) {
        await search(results[0].name.substring(0, 3));
      }
    } catch (err: any) {
      throw err;
    }
  };

  const renderTechnologyItem = (technology: TechnologyListItem) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ScienceIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {technology.name}
          </Typography>
        </Box>
        {technology.classification && (
          <Chip label={technology.classification} size="small" color="primary" variant="outlined" />
        )}
      </Box>
      {technology.summary_description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {technology.summary_description.substring(0, 200)}
          {technology.summary_description.length > 200 ? '...' : ''}
        </Typography>
      )}
    </Box>
  );

  return (
    <Container maxWidth="xl" className="py-6">
      {/* Top Toolbar */}
      <Box className="flex items-center justify-between gap-4 mb-6">
        <Typography variant="h4" component="h1" className="font-bold text-gray-800 flex items-center gap-2">
          <ScienceIcon fontSize="large" className="text-blue-600" />
          Technologies
        </Typography>

        <Box className="flex items-center gap-3 flex-1 max-w-2xl">
          <SearchBar
            onSearch={handleSearch}
            placeholder="Search technologies by name..."
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewTechnology}
            size="large"
            className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
          >
            New Technology
          </Button>
        </Box>
      </Box>

      {/* Search Loading Indicator */}
      {searchLoading && results.length === 0 && (
        <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
          <LoadingSpinner message="Searching technologies..." />
        </Paper>
      )}

      {/* Search Results - Show only if we have results */}
      {results.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
            Search Results
          </Typography>

          {searchError && <ErrorMessage message={searchError} />}

          {!searchError && (
            <EntitySearchResults
              results={results}
              onSelect={handleSelectTechnology}
              renderItem={renderTechnologyItem}
            />
          )}
        </Paper>
      )}

      {/* Main Form Section */}
      <Paper elevation={2} className="p-6">
        <Typography variant="h5" className="mb-4 font-semibold text-gray-700 flex items-center gap-2">
          {selectedTechnology ? (
            <>
              <ScienceIcon className="text-blue-600" />
              Edit Technology: {selectedTechnology.name}
            </>
          ) : (
            <>
              <AddIcon className="text-blue-600" />
              Create New Technology
            </>
          )}
        </Typography>

        {loadingTechnology && <LoadingSpinner message="Loading technology..." />}

        {error && <ErrorMessage message={error} onDismiss={() => setError(null)} />}

        {!loadingTechnology && (
          <TechnologyForm
            technology={selectedTechnology}
            onSave={handleSave}
            onDelete={handleDelete}
            onCancel={handleNewTechnology}
          />
        )}
      </Paper>
    </Container>
  );
};

export default TechnologiesPage;
